import React, { useEffect, useState } from "react";
import { makeStyles, useTheme } from "@seahorse/ui";
import { fetchUserData } from "./Utils/Utils";

interface ContextProp {
  currencyList: string[]; // Ensure that the type of the prop is defined
}
const CoTraderUserProfileTile: React.FunctionComponent<ContextProp> = ({
  currencyList,
}) => {
  const theme = useTheme();

  const useStyles = makeStyles({
    root: {
      display: "flex",
      flexDirection: "column",
      height: "100%",
      width: "100%",
      padding: "20px",
      backgroundColor: theme.globallink.colors.DarkGrey[500],
      color: theme.palette.text.primary,
      overflowY: "auto",
    },
    card: {
      backgroundColor: theme.palette.background.paper,
      padding: "24px",
      borderRadius: "12px",
      boxShadow: "0px 6px 12px rgba(0, 0, 0, 0.08)",
      display: "flex",
      flexDirection: "column",
      gap: "20px",
    },
    header: {
      fontSize: "22px",
      fontWeight: 600,
      color: theme.palette.primary.main,
      borderBottom: `1px solid ${theme.palette.divider}`,
      paddingBottom: "12px",
      marginBottom: "8px",
    },
    grid: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      rowGap: "16px",
      columnGap: "32px",
    },
    fieldBlock: {
      display: "flex",
      flexDirection: "column",
    },
    label: {
      fontSize: "12px",
      fontWeight: 500,
      color: theme.palette.text.secondary,
      marginBottom: "4px",
      textTransform: "uppercase",
    },
    value: {
      fontSize: "15px",
      fontWeight: 500,
      color: theme.palette.text.primary,
      backgroundColor: theme.palette.background.default,
      padding: "10px 12px",
      borderRadius: "6px",
      border: `1px solid ${theme.palette.divider}`,
    },
    select: {
      fontSize: "15px",
      padding: "10px 12px",
      borderRadius: "6px",
      border: `1px solid ${theme.palette.divider}`,
      backgroundColor: theme.palette.background.default,
      color: theme.palette.text.primary,
    },
  });

  const classes = useStyles();

  const [userProfile, setUserProfile] = useState<any>(null);
  const [defaultCurrency, setDefaultCurrency] = useState<string>(
    localStorage.getItem("default_currency") || "USDJPY"
  );
  const [defaultLanguage, setDefaultLanguage] = useState<string>(
    localStorage.getItem("user_language") || "English"
  );

  useEffect(() => {
    fetchUserData(() => {}, setUserProfile);
  }, []);

  const handleCurrencyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newValue = e.target.value;
    setDefaultCurrency(newValue);
    localStorage.setItem("default_currency", newValue);
  };

  const handleLanguageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newValue = e.target.value;
    setDefaultLanguage(newValue);
    localStorage.setItem("user_language", newValue);
  };

  return (
    <div className={classes.root}>
      <div className={classes.card}>
        <div className={classes.header}>User Profile</div>
        {userProfile && (
          <div className={classes.grid}>
            <div className={classes.fieldBlock}>
              <div className={classes.label}>First Name</div>
              <div className={classes.value}>
                {userProfile.first_name || "N.A."}
              </div>
            </div>
            <div className={classes.fieldBlock}>
              <div className={classes.label}>Last Name</div>
              <div className={classes.value}>
                {userProfile.last_name || "N.A."}
              </div>
            </div>
            <div className={classes.fieldBlock}>
              <div className={classes.label}>Email</div>
              <div className={classes.value}>{userProfile.email || "N.A."}</div>
            </div>
            <div className={classes.fieldBlock}>
              <div className={classes.label}>Secondary Contact</div>
              <div className={classes.value}>
                {userProfile.secondary_contacts || "N.A."}
              </div>
            </div>
            <div className={classes.fieldBlock}>
              <div className={classes.label}>Organization</div>
              <div className={classes.value}>
                {userProfile.organization || "N.A."}
              </div>
            </div>
            <div className={classes.fieldBlock}>
              <div className={classes.label}>Role</div>
              <div className={classes.value}>{userProfile.role || "N.A."}</div>
            </div>

            {/* Default Currency Dropdown */}
            <div className={classes.fieldBlock}>
              <div className={classes.label}>Default Currency</div>
              <select
                className={classes.select}
                value={defaultCurrency}
                onChange={(e) => {
                  const newCurrency = e.target.value;
                  setDefaultCurrency(newCurrency);
                  localStorage.setItem("default_currency", newCurrency);
                  window.dispatchEvent(new Event("storage")); // Optional
                }}
              >
                {[...currencyList]
                  .sort((a, b) => a.localeCompare(b))
                  .map((pair) => (
                    <option key={pair} value={pair}>
                      {pair}
                    </option>
                  ))}
              </select>
            </div>

            {/* Default Language Dropdown */}
            <div className={classes.fieldBlock}>
              <div className={classes.label}>Default Language</div>
              <select
                className={classes.select}
                value={defaultLanguage}
                onChange={(e) => {
                  const newLang = e.target.value;
                  setDefaultLanguage(newLang);
                  localStorage.setItem("user_language", newLang); // unified logic
                  window.dispatchEvent(new Event("storage"));
                }}
              >
                {[
                  "English",
                  "Hindi",
                  "Chinese",
                  "Japanese",
                  "Korean",
                  "Indonesian",
                  "Vietnamese",
                  "Thai",
                ].map((lang) => (
                  <option key={lang} value={lang}>
                    {lang}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CoTraderUserProfileTile;
